/**
 * Client-side mirror of worker/src/db/enums.ts.
 *
 * The worker bundles its own enum module (different runtime, different
 * package). This file re-declares the same tuples so the client can:
 *
 *   1. Build typed `HeroToolbar` Filter By option lists without importing
 *      from worker/ (which would pull Hono types into the browser bundle).
 *   2. Type-check `<StatusBadge status={...} />` call sites against the
 *      canonical set rather than `string`.
 *
 * IMPORTANT: keep this file in lockstep with worker/src/db/enums.ts. The
 * StatusBadge map in components/StatusBadge.tsx covers the labels; this
 * file covers the keys.
 */

export const ACCOUNT_STATUSES = ["active", "inactive", "prospect"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export const QUOTE_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "converted",
  "rejected",
  "expired",
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const ORDER_STATUSES = [
  "pending",
  "submitted",
  "confirmed",
  "partially_fulfilled",
  "fulfilled",
  "partially_shipped",
  "fully_shipped",
  "partially_delivered",
  "fully_delivered",
  "invoiced",
  "closed",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PROCUREMENT_STATUSES = [
  "not_required",
  "pending_po",
  "po_submitted",
  "po_confirmed",
  "in_production",
  "ready_to_ship",
  "partially_shipped",
  "fully_shipped",
  "issue",
] as const;
export type ProcurementStatus = (typeof PROCUREMENT_STATUSES)[number];

export const FULFILLMENT_STATUSES = [
  "pending",
  "partial",
  "allocated",
  "fulfilled",
] as const;
export type FulfillmentStatus = (typeof FULFILLMENT_STATUSES)[number];

export const PO_STATUSES = [
  "draft",
  "submitted",
  "acknowledged",
  "confirmed",
  "in_production",
  "partially_received",
  "received",
  "closed",
  "cancelled",
  "issue",
] as const;
export type PurchaseOrderStatus = (typeof PO_STATUSES)[number];

export const INVOICE_STATUSES = [
  "draft",
  "sent",
  "deposit_requested",
  "overdue",
  "partially_paid",
  "paid",
  "voided",
  "closed",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const SHIPMENT_STATUSES = [
  "PLANNED",
  "BOOKED",
  "PICKED_UP",
  "IN_TRANSIT",
  "AT_PORT_OF_ORIGIN",
  "ON_WATER",
  "AT_PORT_OF_DESTINATION",
  "IN_CUSTOMS",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "PARTIALLY_DELIVERED",
  "EXCEPTION",
  "CANCELLED",
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export const OPPORTUNITY_STAGES = [
  "prospecting",
  "qualification",
  "proposal",
  "negotiation",
  "closed_won",
  "closed_lost",
] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

export const CONTRACT_STATUSES = [
  "draft",
  "active",
  "expired",
  "terminated",
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const PRODUCT_CLASSES = ["stock", "custom"] as const;
export type ProductClass = (typeof PRODUCT_CLASSES)[number];

/**
 * Helper: turn an enum tuple into a `HeroToolbarOption[]` for the toolbar
 * Filter By menu. The label is title-cased with underscores → spaces.
 */
export function enumToOptions<T extends string>(
  values: readonly T[]
): { value: T; label: string }[] {
  return values.map((value) => ({
    value,
    label: value
      .split(/[_\s]/g)
      .map((part) =>
        part.length === 0 ? "" : part[0].toUpperCase() + part.slice(1).toLowerCase()
      )
      .join(" "),
  }));
}
